import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { AppDispatch, RootState } from "@/app/store";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { resolveNotificationSource } from "../config/notification-sources.config";
import { notificationsSocketService } from "../services/notifications-socket.service";
import { DEFAULT_NOTIFICATIONS_LIMIT } from "../services/notifications.service";
import {
  fetchNotifications,
  fetchUnreadNotifications,
  markNotificationAsRead,
  pushNotification,
  setConnectionStatus,
  selectUnreadNotificationsCount,
  setNotificationRead,
  resetNotificationsState,
} from "../store/notificationsSlice";
import { stopNotificationSoundLoop, syncNotificationReminder } from "../utils/playNotificationSound";

/** How long a read sent over the socket waits for the server's ok before it is sent again over REST. */
const READ_ACK_TIMEOUT_MS = 5_000;

export function useNotifications() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const { t } = useTranslations("global");
  const source = user ? resolveNotificationSource(user.user_type) : null;
  const enabled = !!source?.enabled;
  const restListEndpoint = source?.restListEndpoint ?? null;
  const restMarkReadEndpoint = source?.restMarkReadEndpoint ?? null;

  const { items, total, hasMore, isLoading, error, connectionStatus } = useSelector(
    (state: RootState) => state.notifications,
  );

  // The server's count, so a pending notification older than the loaded page still shows (and sounds).
  const unreadCount = useSelector(selectUnreadNotificationsCount);
  const pendingReads = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const loadMore = useCallback(() => {
    if (!enabled || !restListEndpoint) return;
    dispatch(
      fetchNotifications({
        skip: items.length,
        limit: DEFAULT_NOTIFICATIONS_LIMIT,
        endpoint: restListEndpoint,
      }),
    );
  }, [dispatch, items.length, enabled, restListEndpoint]);

  const loadList = useCallback(() => {
    if (!enabled || !restListEndpoint) return;
    dispatch(
      fetchNotifications({
        skip: 0,
        limit: DEFAULT_NOTIFICATIONS_LIMIT,
        endpoint: restListEndpoint,
      }),
    );
    dispatch(fetchUnreadNotifications(restListEndpoint));
  }, [dispatch, enabled, restListEndpoint]);

  const markOverRest = useCallback(
    (id: string) => {
      dispatch(markNotificationAsRead({ id, endpoint: restMarkReadEndpoint?.(id) }))
        .unwrap()
        .catch(() => colocaToast.error(t("header_notifications_mark_read_failed")));
    },
    [dispatch, restMarkReadEndpoint, t],
  );

  // Over the socket while it is connected, over REST otherwise. A socket can look connected and be dead, so a
  // read the server does not confirm in a few seconds goes over REST too; marking twice is harmless.
  const markAsRead = useCallback(
    (id: string) => {
      if (!enabled || pendingReads.current.has(id)) return;
      if (!notificationsSocketService.markAsRead(id)) {
        markOverRest(id);
        return;
      }
      const timer = setTimeout(() => {
        pendingReads.current.delete(id);
        markOverRest(id);
      }, READ_ACK_TIMEOUT_MS);
      pendingReads.current.set(id, timer);
    },
    [enabled, markOverRest],
  );

  useEffect(() => {
    const pending = pendingReads.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = notificationsSocketService.onNotification((notification, eventName) => {
      if (eventName === "notification:created") {
        dispatch(pushNotification(notification));
      }
    });
    return unsubscribe;
  }, [dispatch, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = notificationsSocketService.onMarkReadOk((notificationId) => {
      const timer = pendingReads.current.get(notificationId);
      if (timer) {
        clearTimeout(timer);
        pendingReads.current.delete(notificationId);
      }
      dispatch((next, getState) => {
        // The count follows a read of any notification the server listed as unread. Only one beyond what it
        // listed (more than 200 pending) leaves the count unsure, and then it is read again.
        const { unread } = getState().notifications;
        const beyondListed =
          !!unread && unread.total > unread.ids.length && !unread.ids.includes(notificationId);
        next(setNotificationRead(notificationId));
        if (beyondListed && restListEndpoint) next(fetchUnreadNotifications(restListEndpoint));
      });
    });
    return unsubscribe;
  }, [dispatch, enabled, restListEndpoint]);

  // Nothing pushed while the connection was down is replayed: the list and the count are read again.
  useEffect(() => {
    if (!enabled) return;
    return notificationsSocketService.onReconnect(loadList);
  }, [enabled, loadList]);

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = notificationsSocketService.onError((message) => {
      colocaToast.error(message);
    });
    return unsubscribe;
  }, [enabled]);

  return {
    items,
    total,
    hasMore,
    isLoading,
    error,
    connectionStatus,
    unreadCount,
    loadList,
    loadMore,
    markAsRead,
  };
}

/**
 * The reminder while something is pending (`syncNotificationReminder`): every
 * `NOTIFICATION_SOUND_REPEAT_INTERVAL_MS` from the moment something is, never restarted by a change in the
 * count.
 */
export function useNotificationSoundLoop(enabled: boolean = true): void {
  const unreadCount = useSelector(selectUnreadNotificationsCount);

  useEffect(() => {
    syncNotificationReminder(unreadCount, enabled);
  }, [enabled, unreadCount]);

  useEffect(() => () => stopNotificationSoundLoop(), []);
}

export function useNotificationsSocket(isAuthenticated: boolean) {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();

  useEffect(() => {
    notificationsSocketService.setCurrentUser(user ? { user_type: user.user_type, role: user.role } : null);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) {
      notificationsSocketService.disconnect();
      notificationsSocketService.setOnStatusChange(null);
      dispatch(setConnectionStatus("disconnected"));
      dispatch(resetNotificationsState());
      return;
    }

    notificationsSocketService.setOnStatusChange((status) => {
      dispatch(setConnectionStatus(status));
    });

    dispatch(setConnectionStatus("connecting"));
    notificationsSocketService.connect();

    return () => {
      notificationsSocketService.disconnect();
      notificationsSocketService.setOnStatusChange(null);
      dispatch(setConnectionStatus("disconnected"));
    };
  }, [isAuthenticated, dispatch]);
}
